/* Sfeervuur premium header: sticky shrink, mega panel, mobile drawer, cursor picker mount.
   Markup: sections/sf-header.liquid   Styles: assets/sf-header.css */
(() => {
  'use strict';

  if (customElements.get('sf-header')) return;

  const mqDesktop = window.matchMedia('(min-width: 990px)');

  class SFHeader extends HTMLElement {
    connectedCallback() {
      this._abort = new AbortController();
      const opts = { signal: this._abort.signal };
      const passive = { passive: true, signal: this._abort.signal };

      this.bar = this.querySelector('.sf-header__bar');
      this.drawer = this.querySelector('.sf-drawer');
      this.burger = this.querySelector('.sf-burger');

      this._initScroll(passive);
      this._initMeasure(opts);
      this._initPanels(opts);
      this._initDrawer(opts);

      if (window.__sfCursor) window.__sfCursor.mountPicker(this.querySelector('[data-sf-cursor-picker]'));
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._ro) this._ro.disconnect();
      document.documentElement.classList.remove('sf-lock');
    }

    /* ---- shrink on scroll ---- */

    _initScroll(passive) {
      // Horizon scrolls `.page-wrapper` on desktop (>= 990px) and the document on mobile.
      const wrapper = document.querySelector('.page-wrapper');
      const progress = this.querySelector('.sf-header__progress');
      let ticking = false;
      let scrolled = false;
      let shown = -1;

      const update = () => {
        ticking = false;
        const y = Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
        const max = Math.max(
          wrapper ? wrapper.scrollHeight - wrapper.clientHeight : 0,
          document.documentElement.scrollHeight - window.innerHeight
        );
        // written on the progress line itself: a CSS variable on <sf-header> restyled the whole header
        // on every frame of scrolling
        const p = max > 0 ? Math.min(1, y / max) : 0;
        if (progress && Math.abs(p - shown) > 0.0005) {
          shown = p;
          progress.style.transform = `scaleX(${p.toFixed(4)})`;
        }
        const next = y > 24;
        if (next === scrolled) return;
        scrolled = next;
        this.dataset.scrolled = String(next);
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      window.addEventListener('scroll', onScroll, passive);
      if (wrapper) wrapper.addEventListener('scroll', onScroll, passive);

      update();
    }

    /* ---- keep Horizon's layout variables in sync (heroes, sticky product blocks) ---- */

    _initMeasure(opts) {
      // These variables sit on <body>, so every change restyles the whole page. Write them only when a
      // value really changed, and not on every frame while the bar animates its height on scroll
      // (0.55 s): the observer below waits until the size has settled.
      const written = {};
      const put = (el, name, value) => {
        const key = `${el === document.body ? 'b' : 'g'}${name}`;
        if (written[key] === value) return;
        written[key] = value;
        el.style.setProperty(name, value);
      };
      const measure = () => {
        const group = document.getElementById('header-group');
        const barHeight = this.bar ? this.bar.offsetHeight : 0;
        let announce = 0;
        let total = 0;

        if (group) {
          for (const el of group.children) {
            if (!(el instanceof HTMLElement)) continue;
            total += el.offsetHeight;
            if (!el.contains(this)) announce += el.offsetHeight;
          }
          put(group, '--sf-announce-h', `${announce}px`);
        }

        put(document.body, '--sf-announce-h', `${announce}px`);
        put(document.body, '--header-height', `${barHeight}px`);
        put(document.body, '--header-group-height', `${total || barHeight}px`);
      };

      let settle = 0;
      this._ro = new ResizeObserver(() => {
        window.clearTimeout(settle);
        settle = window.setTimeout(measure, 120);
      });
      if (this.bar) this._ro.observe(this.bar);
      const group = document.getElementById('header-group');
      if (group) [...group.children].forEach((el) => this._ro.observe(el));

      window.addEventListener('load', measure, { ...opts, once: true });
      measure();
    }

    /* ---- desktop mega panel ---- */

    _initPanels(opts) {
      const items = [...this.querySelectorAll('.sf-nav__item--panel')];

      const anyOpen = () => !!this.querySelector('.sf-panel[data-open="true"]');
      const syncScrim = () => {
        if (anyOpen()) this.setAttribute('data-panel-open', 'true');
        else this.removeAttribute('data-panel-open');
      };

      items.forEach((item) => {
        const toggle = item.querySelector('.sf-nav__toggle');
        const panel = item.querySelector('.sf-panel');
        if (!toggle || !panel) return;

        let openTimer = 0;
        let closeTimer = 0;

        const open = () => {
          window.clearTimeout(closeTimer);
          items.forEach((other) => {
            if (other !== item && other._sfClose) other._sfClose();
          });
          panel.dataset.open = 'true';
          panel.inert = false;
          toggle.setAttribute('aria-expanded', 'true');
          syncScrim();
        };

        const close = () => {
          window.clearTimeout(openTimer);
          panel.dataset.open = 'false';
          panel.inert = true;
          toggle.setAttribute('aria-expanded', 'false');
          syncScrim();
        };

        item._sfClose = close;

        item.addEventListener(
          'pointerenter',
          (e) => {
            if (e.pointerType === 'touch') return;
            window.clearTimeout(closeTimer);
            openTimer = window.setTimeout(open, 80);
          },
          opts
        );

        item.addEventListener(
          'pointerleave',
          (e) => {
            if (e.pointerType === 'touch') return;
            window.clearTimeout(openTimer);
            closeTimer = window.setTimeout(close, 220);
          },
          opts
        );

        toggle.addEventListener('click', () => (panel.dataset.open === 'true' ? close() : open()), opts);

        item.addEventListener(
          'focusout',
          (e) => {
            if (!item.contains(e.relatedTarget)) close();
          },
          opts
        );

        item.addEventListener(
          'keydown',
          (e) => {
            if (e.key === 'Escape' && panel.dataset.open === 'true') {
              close();
              toggle.focus();
            }
          },
          opts
        );
      });

      document.addEventListener(
        'pointerdown',
        (e) => {
          if (!this.contains(e.target)) items.forEach((item) => item._sfClose && item._sfClose());
        },
        opts
      );
    }

    /* ---- mobile drawer ---- */

    _initDrawer(opts) {
      const { drawer, burger } = this;
      if (!drawer || !burger) return;

      const closeBtn = drawer.querySelector('.sf-drawer__close');

      const open = () => {
        drawer.dataset.open = 'true';
        drawer.inert = false;
        burger.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('sf-lock');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      };

      const close = (returnFocus = true) => {
        drawer.dataset.open = 'false';
        drawer.inert = true;
        burger.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('sf-lock');
        if (returnFocus) burger.focus({ preventScroll: true });
      };

      burger.addEventListener('click', open, opts);
      if (closeBtn) closeBtn.addEventListener('click', () => close(), opts);

      drawer.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'Escape') close();
        },
        opts
      );

      drawer.addEventListener(
        'click',
        (e) => {
          if (e.target instanceof Element && e.target.closest('a[href]')) close(false);
        },
        opts
      );

      drawer.querySelectorAll('.sf-drawer__item--sub').forEach((item) => {
        const toggle = item.querySelector('.sf-drawer__toggle');
        const sub = item.querySelector('.sf-drawer__sub');
        if (!toggle || !sub) return;

        sub.inert = true;
        toggle.addEventListener(
          'click',
          () => {
            const expand = toggle.getAttribute('aria-expanded') !== 'true';
            toggle.setAttribute('aria-expanded', String(expand));
            sub.dataset.open = String(expand);
            sub.inert = !expand;
          },
          opts
        );
      });

      const onChange = () => {
        if (mqDesktop.matches && drawer.dataset.open === 'true') close(false);
      };
      if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', onChange, opts);
    }
  }

  customElements.define('sf-header', SFHeader);

  // language switch links (snippets/sf-lang.liquid) keep the page's query string: search terms, sorting
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element ? e.target.closest('a[data-sf-lang]') : null;
    if (a && window.location.search && !a.search) a.search = window.location.search;
  });
})();
